import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { format, addDays, startOfWeek, addWeeks, subWeeks, parseISO, isSameDay } from 'date-fns';
import { de } from 'date-fns/locale';
import { Calendar, ChevronLeft, ChevronRight, X, User, Plus, Mail, Eye, ArrowRight, Send, Grid2x2, Keyboard, CheckCircle2, UserX, RotateCcw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from '../../../components/ui/dropdown-menu';
import { generateTimeSlots, heute } from '../../../lib/settings';
import { toast } from 'sonner';
import { Textarea } from '../../../components/ui/textarea';
import { Input } from '../../../components/ui/input';
import { lehrerApi, type LehrerOption, unterrichtseinheitenApi, unitIdOf } from '../../../lib/unterrichtseinheiten';
import { personTypesApi } from '../../../lib/person-types';
import TrialWizardModal from '../../../components/trials/TrialWizardModal';
import { Badge } from '../../../components/ui/badge';
import Legend from '../../../components/schedule/Legend';
import FullScheduleGrid from '../../../components/schedule/FullScheduleGrid';
import ResourceMatrixView from '../../../components/schedule/ResourceMatrixView';
import ScheduleToolbar, { type GroupParticipantFilter, type ScheduleStatus } from '../../../components/schedule/ScheduleToolbar';
import { useTeacherSchedule, type EnhancedCourse } from '../../../hooks/useTeacherSchedule';
import { useTeacherTimetable } from '../../../hooks/useTeacherTimetable';
import { useScheduleViewportDensity } from '../../../hooks/useScheduleViewportDensity';
import { fetchTrials, rescheduleTrial as rescheduleTrialApi, stehtImTerminplan, type TrialLessonDTO } from '../../../lib/trials';
import { personenApi, type TeacherAvailabilitySlot } from '../../../lib/personen';
import { useStandort } from '../../../components/layouts/use-standort';
import { useStandortState } from '../../../hooks/useStandortState';
import CourseDetailsWizard, { type CourseDetailsEvent } from '../../../components/schedule/CourseDetailsWizard';
import TeacherChangeWizardModal, { type TeacherChangeWizardSeed } from '../../../components/schedule/TeacherChangeWizardModal';
import CourseCancelWizardModal, { type CourseCancelSeed } from '../../../components/schedule/CourseCancelWizardModal';
import CourseRescheduleWizardModal, { type CourseRescheduleSeed } from '../../../components/schedule/CourseRescheduleWizardModal';
import RescheduleTrialModal from '../../../components/trials/RescheduleTrialModal';
import CancelTrialWizardModal from '../../../components/trials/CancelTrialWizardModal';
import MessageWizardModal, { type MessageRecipient } from '../../../components/communications/MessageWizardModal';
import KuendigungWizardModal from '../../../components/kuendigungen/KuendigungWizardModal';
import { lookupTeilnahmeId } from '../../../lib/kuendigungen';
import { dayEntryKey, deriveTimeGridConfig, durationToHeightPx, layoutDayEntries, layoutOverlappingEvents, overlapLayoutToColumnStyle, timeToTopPx } from '../../../lib/scheduleLayout';
import { scheduleConflictsApi } from '../../../lib/scheduleConflicts';
import { GROUP_COURSE_THRESHOLD, getParticipantCount } from '../../../lib/groupCourses';
import { useScheduleKeyboardShortcuts } from './schedule/useScheduleKeyboardShortcuts';
import CreateCourseDialog from './schedule/CreateCourseDialog';

type BulkLessonStatus = 'abgesagt' | 'durchgeführt' | 'nicht_erschienen' | 'geplant';
const BULK_STATUS_DONE_LABEL: Record<BulkLessonStatus, string> = {
  geplant: 'auf „geplant“ zurückgesetzt',
  abgesagt: 'abgesagt',
  durchgeführt: 'als durchgeführt markiert',
  nicht_erschienen: 'als „nicht erschienen“ markiert',
};

// CourseTooltip ausgelagert in components/schedule/CourseTooltip

// Days of the week
// Context menu types and interfaces
interface ContextMenuPosition {
  x: number;
  y: number;
}
interface ContextMenuState {
  isOpen: boolean;
  position: ContextMenuPosition;
  type: 'course' | 'trial' | 'empty' | 'booking' | 'drop';
  data: unknown; // Course, trial, or time slot data
  day?: Date;
  hour?: number;
  minute?: number;
}

type AvailabilityConfirmAction =
  | { type: 'reschedule_drop'; seed: CourseRescheduleSeed }
  | { type: 'trial_reschedule_drop'; trial: TrialLessonDTO; target: { date: string; dayOfWeek: number; startTime: string; endTime: string } }
  | { type: 'create_trial'; prefill: { teacherId?: string | number; date?: string; startTime?: string; dayOfWeek?: number; roomId?: number; requireDateSelection?: boolean; courseId?: number } }
  | { type: 'create_course'; item: { dayOfWeek: number; hour: string; teacherId: string | number | undefined } };
// Helper function to get contingent color based on percentage remaining
const getContingentColor = (remainingUnits: number, totalUnits: number) => {
  const percentage = remainingUnits / totalUnits * 100;
  if (percentage > 60) return 'text-green-600'; // Good (more than 60% remaining)
  if (percentage > 30) return 'text-yellow-600'; // Warning (30-60% remaining)
  return 'text-red-600'; // Critical (less than 30% remaining)
};
// (unused helper removed)
/** Was gerade gezogen wird, kollidiert nicht mit sich selbst — auch als Mustertermin
 *  oder Probestunde nicht (der Server prüft beide seit TERM-3.2/RAUM-2.2 mit). */
const wanderndeAusnahme = (course: EnhancedCourse | null, trial: TrialLessonDTO | null) => {
  if (course) {
    return {
      excludeCourseId: course.courseId ? Number(course.courseId) : null,
      excludeDate: course.startDate || null,
    };
  }
  const trialId = Number(trial?.id);
  return { excludeTrialId: Number.isFinite(trialId) && trialId > 0 ? trialId : null };
};

const ScheduleManager = () => {
  const { selectedStandortId } = useStandort();
  const { saveState, loadState } = useStandortState();
  const [teacherOptions, setTeacherOptions] = useState<LehrerOption[]>([]);
  const [selectedTeacher, setSelectedTeacher] = useState<string | number | undefined>(undefined);
  const [teacherLabel, setTeacherLabel] = useState<string>('Lehrer');
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(heute(), {
    weekStartsOn: 1
  }));
  // Stundenplan bleibt relativ zur aktuellen Woche statt zu einem fixen Mount-Zeitpunkt.
  const schedulerReferenceWeekKey = format(startOfWeek(heute(), { weekStartsOn: 1 }), 'yyyy-MM-dd');
  const schedulerReferenceDate = useMemo(() => parseISO(`${schedulerReferenceWeekKey}T00:00:00`), [schedulerReferenceWeekKey]);
  
  // Nutze Custom Hook für erweiterte Stundenplan-Daten (Kalenderansicht)
  const { courses: teacherCourses, refetch: refetchSchedule } = useTeacherSchedule(selectedTeacher, currentWeek, selectedStandortId);
  
  // Hook für Stundenplanansicht (relativ zu heute, unabhängig von currentWeek)
  const timetableHookData = useTeacherTimetable(selectedTeacher, schedulerReferenceDate, selectedStandortId);
  // createCourseData/Form state moved into CreateCourseDialog
  // Dialog state muss vor Hooks verwendet werden, die darauf zugreifen
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [trialDialogOpen, setTrialDialogOpen] = useState(false);
  const [trialPrefill, setTrialPrefill] = useState<{ teacherId?: string | number; date?: string; startTime?: string; dayOfWeek?: number; roomId?: number; requireDateSelection?: boolean; courseId?: number }>({});
  const [trialCoursePickOpen, setTrialCoursePickOpen] = useState(false);
  const [trialCoursePickEvent, setTrialCoursePickEvent] = useState<CourseDetailsEvent | null>(null);
  const [courseDetailsOpen, setCourseDetailsOpen] = useState(false);
  const [selectedCourseDetails, setSelectedCourseDetails] = useState<CourseDetailsEvent | null>(null);
  const [teacherChangeOpen, setTeacherChangeOpen] = useState(false);
  const [teacherChangeSeed, setTeacherChangeSeed] = useState<TeacherChangeWizardSeed | null>(null);
  // Drag & Drop state for moving courses
  const [draggedCourse, setDraggedCourse] = useState<EnhancedCourse | null>(null);
  const [draggedTrial, setDraggedTrial] = useState<TrialLessonDTO | null>(null);
  const [activeDropTarget, setActiveDropTarget] = useState<{ dayKey: string; hour: number; minute: number } | null>(null);
  const [dropConflictPreview, setDropConflictPreview] = useState<{
    dayKey: string;
    hour: number;
    minute: number;
    status: 'checking' | 'ok' | 'conflict' | 'availability_override' | 'error';
    message?: string;
  } | null>(null);
  // Load teacher label from person types
  useEffect(() => {
    personTypesApi.list().then(types => {
      const teacherType = types.find(t => t.code === 'teacher');
      if (teacherType?.name) setTeacherLabel(teacherType.name);
    }).catch(() => { /* ignore */ });
  }, []);

  // Load teachers from backend (strikt nach aktivem Standort)
  useEffect(() => {
    let cancelled = false;
    const loadTeachers = async () => {
      if (!selectedStandortId) {
        if (!cancelled) {
          setTeacherOptions([]);
          setSelectedTeacher(undefined);
        }
        return;
      }
      try {
        const list = await lehrerApi.list({ standortId: Number(selectedStandortId) });
        if (cancelled) return;
        setTeacherOptions(list);

        // Lade gespeicherten selectedTeacher aus StandortState
        const savedTeacher = loadState('selectedTeacher');

        // Wenn der aktuell ausgewählte Lehrer nicht mehr in der Liste ist, gespeicherten oder ersten Lehrer auswählen
        if (selectedTeacher && !list.find(t => String(t.id) === String(selectedTeacher))) {
          if (savedTeacher && list.find(t => String(t.id) === String(savedTeacher))) {
            setSelectedTeacher(String(savedTeacher));
          } else if (list.length > 0) {
            setSelectedTeacher(String(list[0].id));
          } else {
            setSelectedTeacher(undefined);
          }
        } else if (!selectedTeacher) {
          // Kein Lehrer ausgewählt: versuche gespeicherten zu laden, sonst ersten
          if (savedTeacher && list.find(t => String(t.id) === String(savedTeacher))) {
            setSelectedTeacher(String(savedTeacher));
          } else if (list.length > 0) {
            setSelectedTeacher(String(list[0].id));
          } else {
            setSelectedTeacher(undefined);
          }
        }
      } catch {
        if (!cancelled) {
          setTeacherOptions([]);
          setSelectedTeacher(undefined);
        }
      }
    };
    void loadTeachers();
    return () => {
      cancelled = true;
    };
  }, [selectedStandortId, loadState, selectedTeacher]);

  // Speichere selectedTeacher im StandortState, wenn er sich ändert
  useEffect(() => {
    if (selectedTeacher !== undefined) {
      saveState('selectedTeacher', selectedTeacher);
    }
  }, [selectedTeacher, saveState]);

  const [activeView, setActiveView] = useState('scheduler');
  const [teacherAvailability, setTeacherAvailability] = useState<TeacherAvailabilitySlot[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showTrials, setShowTrials] = useState(true);
  const [showExternalBlocked, setShowExternalBlocked] = useState(true);
  const [showOnlyGroupCourses, setShowOnlyGroupCourses] = useState(false);
  const [groupParticipantFilter, setGroupParticipantFilter] = useState<GroupParticipantFilter>('all');
  const [activeStatuses, setActiveStatuses] = useState<Record<ScheduleStatus, boolean>>({
    aktiv: true,
    geplant: true,
    verschoben: true,
    abgesagt: true,
    durchgeführt: true,
    nicht_erschienen: true,
    // Vom Nachtjob abgeschlossene Termine verschwanden sonst aus dem Plan (T-0085).
    abgeschlossen: true,
  });
  const { rowPx, gridMaxHeight } = useScheduleViewportDensity();
  // Get time slots based on settings
  const timeSlots = useMemo(() => generateTimeSlots(), []);
  // Context menu state
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    isOpen: false,
    position: {
      x: 0,
      y: 0
    },
    type: 'empty',
    data: null
  });
  // Dialog states
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<unknown>(null);
  // Course cancel / reschedule wizards (extracted)
  const [cancelCourseOpen, setCancelCourseOpen] = useState(false);
  const [cancelCourseSeed, setCancelCourseSeed] = useState<CourseCancelSeed | null>(null);
  const [rescheduleCourseOpen, setRescheduleCourseOpen] = useState(false);
  const [rescheduleCourseSeed, setRescheduleCourseSeed] = useState<CourseRescheduleSeed | null>(null);
  const [rescheduleTrialOpen, setRescheduleTrialOpen] = useState(false);
  const [rescheduleTrialSeed, setRescheduleTrialSeed] = useState<TrialLessonDTO | null>(null);
  const [rescheduleTrialTarget, setRescheduleTrialTarget] = useState<{ date: string; dayOfWeek: number; startTime: string; endTime: string } | null>(null);
  const [cancelTrialOpen, setCancelTrialOpen] = useState(false);
  const [cancelTrialSeed, setCancelTrialSeed] = useState<TrialLessonDTO | null>(null);
  // Cancel wizard state
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false);
  const [kuendigungWizardOpen, setKuendigungWizardOpen] = useState(false);
  const [kuendigungTeilnahmeId, setKuendigungTeilnahmeId] = useState<number>(0);
  const [changeCourseDialogOpen, setChangeCourseDialogOpen] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<string | null>(null);
  const [contactMethod, setContactMethod] = useState('email');
  const [messageText, setMessageText] = useState('');
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>([]);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelectedCourseIds, setBulkSelectedCourseIds] = useState<string[]>([]);
  const [bulkUpdatingStatus, setBulkUpdatingStatus] = useState<BulkLessonStatus | null>(null);
  const [availabilityConfirmOpen, setAvailabilityConfirmOpen] = useState(false);
  const [availabilityConfirmMessage, setAvailabilityConfirmMessage] = useState('');
  const [availabilityConfirmAction, setAvailabilityConfirmAction] = useState<AvailabilityConfirmAction | null>(null);

  const [messageOpen, setMessageOpen] = useState(false);
  // Aus den Kursdetails heraus an eine Person: SMS oder E-Mail vorgewählt (T-0295).
  const [messageChannel, setMessageChannel] = useState<'email' | 'sms'>('email');
  const [messageTitle, setMessageTitle] = useState('Rundmail an Teilnehmer');
  useEffect(() => {
    if (messageOpen) return;
    setMessageChannel('email');
    setMessageTitle('Rundmail an Teilnehmer');
  }, [messageOpen]);
  const [messageRecipients, setMessageRecipients] = useState<MessageRecipient[]>([]);
  // Reference to the calendar container for positioning
  const calendarRef = useRef<HTMLDivElement>(null);
  // Generate week days for the current week (Kalenderansicht)
  const calendarWeekDays = useMemo(() => {
    return Array.from({
      length: 7
    }, (_, i) => addDays(currentWeek, i));
  }, [currentWeek]);
  
  // Generate week days for scheduler view (relativ zu heute)
  const scheduleWeekDays = useMemo(() => {
    const weekStart = startOfWeek(schedulerReferenceDate, { weekStartsOn: 1 });
    return Array.from({
      length: 7
    }, (_, i) => addDays(weekStart, i));
  }, [schedulerReferenceDate]);
  
  // Trials für Kalenderansicht (abhängig von currentWeek)
  const [calendarTrials, setCalendarTrials] = useState<TrialLessonDTO[]>([]);
  
  // Trials für Stundenplanansicht (relativ zu heute, unabhängig von currentWeek)
  // Verwende Trials aus timetableHookData, der bereits relativ zu heute lädt
  const scheduleTrials = timetableHookData.trials;
  
  const refetchCalendarTrials = useCallback(async () => {
    try {
      if (!selectedStandortId) {
        setCalendarTrials([]);
        return;
      }
      const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
      const weekEnd = addDays(weekStart, 6);
      const start = format(weekStart, 'yyyy-MM-dd');
      const end = format(addDays(currentWeek, 84), 'yyyy-MM-dd'); // ca. 12 Wochen in die Zukunft für Kalenderansicht
      const items = await fetchTrials(start, end, Number(selectedStandortId));
      const filteredBase = items.filter(t => {
        const d = parseISO(`${t.date}T00:00:00`);
        return d >= weekStart && d <= weekEnd;
      });
      const filtered = selectedTeacher ? filteredBase.filter(t => String(t.teacherId || '') === String(selectedTeacher)) : filteredBase;
      setCalendarTrials(filtered);
    } catch {
      setCalendarTrials([]);
    }
  }, [currentWeek, selectedStandortId, selectedTeacher]);

  useEffect(() => {
    refetchCalendarTrials();
  }, [refetchCalendarTrials]);

  useEffect(() => {
    let cancelled = false;
    const loadTeacherAvailability = async () => {
      if (!selectedTeacher || !selectedStandortId) {
        setTeacherAvailability([]);
        return;
      }
      const selectedTeacherOption = teacherOptions.find((t) => String(t.id) === String(selectedTeacher));
      const wpUserId = selectedTeacherOption?.wp_user_id;
      if (!wpUserId) {
        setTeacherAvailability([]);
        return;
      }
      try {
        const slots = await personenApi.getTeacherAvailability(wpUserId, Number(selectedStandortId));
        if (!cancelled) setTeacherAvailability(Array.isArray(slots) ? slots : []);
      } catch {
        if (!cancelled) setTeacherAvailability([]);
      }
    };
    loadTeacherAvailability();
    return () => {
      cancelled = true;
    };
  }, [selectedTeacher, selectedStandortId, teacherOptions]);

  const hhmmToMinutes = useCallback((value: string): number => {
    const [h, m] = (value || '').slice(0, 5).split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
  }, []);

  const isWithinTeacherAvailabilityRange = useCallback((day: Date, startHHMM: string, endHHMM: string): boolean => {
    if (!teacherAvailability || teacherAvailability.length === 0) return true;
    const dayOfWeek = ((day.getDay() + 6) % 7) + 1;
    const daySlots = teacherAvailability.filter((s) => Number(s.dayOfWeek) === dayOfWeek);
    if (daySlots.length === 0) return false;
    const start = hhmmToMinutes(startHHMM);
    const end = hhmmToMinutes(endHHMM);
    return daySlots.some((slot) => {
      const slotStart = hhmmToMinutes(slot.time_start);
      const slotEnd = hhmmToMinutes(slot.time_end);
      return start >= slotStart && end <= slotEnd;
    });
  }, [teacherAvailability, hhmmToMinutes]);

  const requestAvailabilityOverrideConfirm = useCallback((
    message: string,
    action: AvailabilityConfirmAction
  ) => {
    setAvailabilityConfirmMessage(message);
    setAvailabilityConfirmAction(action);
    setAvailabilityConfirmOpen(true);
  }, []);
  
  // Navigate to next week
  const nextWeek = useCallback(() => {
    const newWeek = addWeeks(currentWeek, 1);
    setCurrentWeek(newWeek);
  }, [currentWeek]);
  
  // Reset to current week
  const goToCurrentWeek = () => {
    setCurrentWeek(startOfWeek(heute(), {
      weekStartsOn: 1
    }));
  };
  
  // Auch bei vorheriger Woche materialisieren
  const previousWeek = useCallback(() => {
    setCurrentWeek(prevWeek => subWeeks(prevWeek, 1));
  }, []);
  // Check if a trial is on a specific day
  const isTrialOnDay = (trial: TrialLessonDTO, date: Date) => {
    return isSameDay(parseISO(trial.date), date);
  };
  // Drag handlers for courses
  const onCourseDragStart = (e: React.DragEvent<HTMLDivElement>, course: EnhancedCourse) => {
    setDraggedCourse(course);
    setDraggedTrial(null);
    setActiveDropTarget(null);
    setDropConflictPreview(null);
    try { e.dataTransfer.setData('text/plain', course.id); } catch {
      // Ignore setData failure in restricted drag contexts.
    }
    e.dataTransfer.effectAllowed = 'move';
  };
  const onTrialDragStart = (e: React.DragEvent<HTMLButtonElement>, trial: TrialLessonDTO) => {
    setDraggedTrial(trial);
    setDraggedCourse(null);
    setActiveDropTarget(null);
    setDropConflictPreview(null);
    try { e.dataTransfer.setData('text/plain', String(trial.id)); } catch {
      // Ignore setData failure in restricted drag contexts.
    }
    e.dataTransfer.effectAllowed = 'move';
  };
  const onCourseDragEnd = () => {
    setDraggedCourse(null);
    setDraggedTrial(null);
    setActiveDropTarget(null);
    setDropConflictPreview(null);
  };
  const onTrialDragEnd = () => {
    setDraggedTrial(null);
    setDraggedCourse(null);
    setActiveDropTarget(null);
    setDropConflictPreview(null);
  };
  const handleSlotDragOver = (e: React.DragEvent<HTMLDivElement>, day: Date, hour: number, minute: number) => {
    if (draggedCourse || draggedTrial) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      const dayKey = format(day, 'yyyy-MM-dd');
      setActiveDropTarget((prev) => {
        if (prev && prev.dayKey === dayKey && prev.hour === hour && prev.minute === minute) {
          return prev;
        }
        setDropConflictPreview({ dayKey, hour, minute, status: 'checking' });
        return { dayKey, hour, minute };
      });
    }
  };
  useEffect(() => {
    if ((!draggedCourse && !draggedTrial) || !activeDropTarget) {
      setDropConflictPreview(null);
      return;
    }

    let cancelled = false;
    const dayKey = activeDropTarget.dayKey;
    const hour = activeDropTarget.hour;
    const minute = activeDropTarget.minute;
    const parseToMinutes = (value: string) => {
      const [h, m] = value.split(':').map(Number);
      return h * 60 + m;
    };
    const sourceStart = draggedCourse ? draggedCourse.startTime : (draggedTrial?.startTime || '00:00');
    const sourceEnd = draggedCourse ? draggedCourse.endTime : (draggedTrial?.endTime || '00:30');
    const durationMin = parseToMinutes(sourceEnd) - parseToMinutes(sourceStart);
    const startTotal = hour * 60 + minute;
    const endTotal = startTotal + durationMin;
    const newStart = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    const newEnd = `${String(Math.floor(endTotal / 60)).padStart(2, '0')}:${String(endTotal % 60).padStart(2, '0')}`;
    const sourceTeacherId = draggedCourse ? Number(draggedCourse.teacherId || 0) : Number(draggedTrial?.teacherId || 0);
    const sourceRoomId = draggedCourse ? (draggedCourse.roomId ?? null) : (draggedTrial?.roomId ?? null);
    const sourceId = draggedCourse ? draggedCourse.id : (draggedTrial?.id ?? null);
    const numericUnitId = Number(sourceId);
    const excludeUnitId = Number.isFinite(numericUnitId) ? numericUnitId : null;

    setDropConflictPreview({ dayKey, hour, minute, status: 'checking' });
    const timer = globalThis.setTimeout(async () => {
      try {
        const check = await scheduleConflictsApi.check({
          teacherId: sourceTeacherId,
          roomId: sourceRoomId,
          standortId: selectedStandortId ?? null,
          date: dayKey,
          startTime: newStart,
          endTime: newEnd,
          excludeUnitId: draggedCourse ? excludeUnitId : null,
          ...wanderndeAusnahme(draggedCourse, draggedTrial),
        });
        if (cancelled) return;
        const dayDate = parseISO(`${dayKey}T00:00:00`);
        const availabilityConflict = check.conflicts.find(c => c.type === 'availability_violation');
        const outsideAvailability = availabilityConflict
          ? true
          : !isWithinTeacherAvailabilityRange(dayDate, newStart, newEnd);
        if (outsideAvailability) {
          setDropConflictPreview({
            dayKey,
            hour,
            minute,
            status: 'availability_override',
            message: availabilityConflict?.message
              ?? 'Zeit liegt außerhalb der Lehrer-Freigabe (nur mit Bestätigung möglich).',
          });
        } else if (check.hasConflict) {
          setDropConflictPreview({
            dayKey,
            hour,
            minute,
            status: 'conflict',
            message: check.conflicts[0]?.message || 'Überschneidung erkannt.',
          });
        } else {
          setDropConflictPreview({ dayKey, hour, minute, status: 'ok' });
        }
      } catch {
        if (cancelled) return;
        setDropConflictPreview({ dayKey, hour, minute, status: 'error' });
      }
    }, 140);

    return () => {
      cancelled = true;
      globalThis.clearTimeout(timer);
    };
  }, [draggedCourse, draggedTrial, activeDropTarget, isWithinTeacherAvailabilityRange, selectedStandortId]);
  const handleSlotDrop = async (e: React.DragEvent<HTMLDivElement>, day: Date, hour: number, minute: number) => {
    if (!draggedCourse && !draggedTrial) return;
    e.preventDefault();
    const sourceStart = draggedCourse ? draggedCourse.startTime : (draggedTrial?.startTime || '00:00');
    const sourceEnd = draggedCourse ? draggedCourse.endTime : (draggedTrial?.endTime || '00:30');
    const durationMin = parseTimeToMinutes(sourceEnd) - parseTimeToMinutes(sourceStart);
    const newStart = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    const totalMinutes = hour * 60 + minute + durationMin;
    const newEnd = `${String(Math.floor(totalMinutes / 60)).padStart(2, '0')}:${String(totalMinutes % 60).padStart(2, '0')}`;
    const targetDate = format(day, 'yyyy-MM-dd');
    const targetDayOfWeek = ((day.getDay() + 6) % 7) + 1; // 1=Mo ... 7=So
    const sourceId = draggedCourse ? draggedCourse.id : (draggedTrial?.id ?? null);
    const numericUnitId = Number(sourceId);
    const excludeUnitId = Number.isFinite(numericUnitId) ? numericUnitId : null;

    let hasOverlapConflict = false;
    let overlapMessage = '';
    let checkResult: Awaited<ReturnType<typeof scheduleConflictsApi.check>> | null = null;
    try {
      checkResult = await scheduleConflictsApi.check({
        teacherId: Number((draggedCourse ? draggedCourse.teacherId : draggedTrial?.teacherId) || 0),
        roomId: draggedCourse ? (draggedCourse.roomId ?? null) : (draggedTrial?.roomId ?? null),
        standortId: selectedStandortId ?? null,
        date: targetDate,
        startTime: newStart,
        endTime: newEnd,
        excludeUnitId: draggedCourse ? excludeUnitId : null,
        ...wanderndeAusnahme(draggedCourse, draggedTrial),
      });
      if (checkResult.hasConflict) {
        const hardConflict = checkResult.conflicts.find(c => c.type !== 'availability_violation' || c.severity === 'error');
        if (hardConflict) {
          hasOverlapConflict = true;
          overlapMessage = hardConflict.message || 'Es gibt Überschneidungen für den neuen Slot.';
        }
      }
    } catch {
      // Optional check only; wizard does a second check with detailed output.
    }

    const availabilityConflict = checkResult?.conflicts?.find(c => c.type === 'availability_violation');
    const outsideAvailability = availabilityConflict
      ? true
      : !isWithinTeacherAvailabilityRange(day, newStart, newEnd);
    if (outsideAvailability) {
      toast.warning(availabilityConflict?.message ?? 'Freigabe-Override: Slot liegt außerhalb der Verfügbarkeit. Bestätigung erforderlich.');
    } else if (hasOverlapConflict) {
      toast.warning(`Konflikt erkannt: ${overlapMessage}`);
    }
    if (draggedCourse) {
      const course = draggedCourse;
      const seed: CourseRescheduleSeed = {
        id: course.id,
        courseId: course.courseId,
        ruleId: null,
        name: course.name,
        fachName: course.fachName,
        studentName: course.studentName,
        studentEmail: course.studentEmail,
        teacherId: course.teacherId,
        roomId: course.roomId,
        dayOfWeek: course.dayOfWeek,
        startTime: course.startTime,
        endTime: course.endTime,
        startDate: course.startDate || targetDate,
        sourceType: course.sourceType,
        participants: course.participants,
        dropTarget: {
          date: targetDate,
          dayOfWeek: targetDayOfWeek,
          startTime: newStart,
          endTime: newEnd,
        },
        fromTimetable: activeView === 'scheduler',
      };
      if (outsideAvailability) {
        requestAvailabilityOverrideConfirm(
          `Der Zielslot ${targetDate} ${newStart}–${newEnd} liegt außerhalb der freigegebenen Lehrerzeiten. Hast du das wirklich mit dem Lehrer abgesprochen?`,
          { type: 'reschedule_drop', seed }
        );
      } else {
        setRescheduleCourseSeed(seed);
        setRescheduleCourseOpen(true);
      }
    } else if (draggedTrial) {
      const trial = draggedTrial;
      if (outsideAvailability) {
        requestAvailabilityOverrideConfirm(
          `Der Zielslot ${targetDate} ${newStart}–${newEnd} liegt außerhalb der freigegebenen Lehrerzeiten. Hast du das wirklich mit dem Lehrer abgesprochen?`,
          { type: 'trial_reschedule_drop', trial, target: { date: targetDate, dayOfWeek: targetDayOfWeek, startTime: newStart, endTime: newEnd } }
        );
      } else {
        setRescheduleTrialSeed(trial);
        setRescheduleTrialTarget({ date: targetDate, dayOfWeek: targetDayOfWeek, startTime: newStart, endTime: newEnd });
        setRescheduleTrialOpen(true);
      }
    }
    setDraggedTrial(null);
    setDraggedCourse(null);
    setActiveDropTarget(null);
    setDropConflictPreview(null);
  };
  // Helpers for time calculations
  const parseTimeToMinutes = (t: string): number => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };
  // Handle context menu opening
  const handleContextMenu = (e: React.MouseEvent, type: 'course' | 'trial' | 'empty' | 'booking', data: unknown, day?: Date, hour?: number, minute?: number) => {
    e.preventDefault();
    // Calculate position relative to viewport
    const rect = calendarRef.current?.getBoundingClientRect() || {
      left: 0,
      top: 0
    };
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setContextMenu({
      isOpen: true,
      position: {
        x,
        y
      },
      type,
      data,
      day,
      hour,
      minute
    });
  };
  // Close context menu
  const closeContextMenu = () => {
    setContextMenu(prev => ({
      ...prev,
      isOpen: false
    }));
  };
  // Schließt das Kontextmenü, wenn außerhalb der Terminübersicht geklickt wird.
  useEffect(() => {
    if (!contextMenu.isOpen) return;

    const handlePointerDownOutside = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      // Untermenüs („Teilnehmer“, „Status setzen“) rendert Radix per Portal außerhalb
      // des Kalenders. Ein Klick darin ist kein Klick daneben — sonst schlösse das
      // Menü beim pointerdown, bevor der Klick die Aktion erreicht (TERM-1.4).
      if (target instanceof Element && target.closest('[data-radix-popper-content-wrapper], [role="menu"]')) {
        return;
      }
      const calendarEl = calendarRef.current;
      if (calendarEl && !calendarEl.contains(target)) {
        closeContextMenu();
      }
    };

    document.addEventListener('pointerdown', handlePointerDownOutside);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDownOutside);
    };
  }, [contextMenu.isOpen]);

  // Lehrkraft des Termins (bei Vertretung die vertretende), sonst die gewählte (TERM-8.1).
  const lehrerNameVon = (id: string | number | null | undefined): string | null => {
    const treffer = teacherOptions.find((t) => String(t.id) === String(id ?? ''));
    return treffer ? `${treffer.vorname} ${treffer.nachname}`.trim() : null;
  };

  const openCourseDetails = (course: EnhancedCourse) => {
    closeContextMenu();
    setSelectedCourseDetails({
      unitId: unitIdOf(course.id) ?? 0,
      courseId: course.courseId ?? null,
      roomId: course.roomId ?? null,
      dayOfWeek: course.dayOfWeek,
      name: course.name,
      fachName: course.fachName ?? null,
      startDate: course.startDate,
      startTime: course.startTime,
      endTime: course.endTime,
      raumName: course.raumName ?? null,
      standortAdresse: course.standortAdresse ?? null,
      lehrerName: lehrerNameVon(course.teacherId) ?? lehrerNameVon(selectedTeacher),
      lehrerId: Number(course.teacherId ?? selectedTeacher ?? 0) || null,
      teilnehmer: course.teilnehmer ?? null,
      participants: course.participants ?? null,
      notizen: course.notizen ?? null,
    });
    setCourseDetailsOpen(true);
  };

  const parseCourseIdFromPatternId = (id: unknown): number | null => {
    const s = String(id ?? '');
    const m = s.match(/^pattern-(\d+)-/);
    if (!m) return null;
    const n = Number(m[1]);
    return Number.isFinite(n) ? n : null;
  };

  const toTeacherChangeSeed = (course: EnhancedCourse): TeacherChangeWizardSeed | null => {
    const standortId = selectedStandortId ? Number(selectedStandortId) : 0;
    if (!standortId) {
      toast.error('Kein Standort ausgewählt');
      return null;
    }

    const courseId = course.courseId ?? parseCourseIdFromPatternId(course.id);
    if (!courseId) {
      toast.error('Kurs-ID konnte nicht ermittelt werden');
      return null;
    }

    const anchorStartDate = (course.startDate || '').trim();
    if (!anchorStartDate) {
      toast.error('Startdatum konnte nicht ermittelt werden');
      return null;
    }

    const numericUnitId = Number(course.id);

    return {
      standortId,
      courseId,
      unitId: Number.isFinite(numericUnitId) ? numericUnitId : null,
      launchView: (activeView === 'scheduler' ? 'scheduler' : 'calendar'),
      anchorStartDate,
      anchorStartTime: (course.startTime || '').slice(0, 5) || null,
      anchorEndTime: (course.endTime || '').slice(0, 5) || null,
      courseName: course.name || null,
      fachName: course.fachName ?? null,
      teacherId: selectedTeacher ? Number(selectedTeacher) : null,
    };
  };

  const parseSubprofileRef = (subprofileId: unknown): { wpUserId: number; subIndex: number | null } | null => {
    const raw = String(subprofileId ?? '').trim();
    if (!raw) return null;
    if (raw.includes('-')) {
      const [wpRaw, subRaw] = raw.split('-', 2);
      const wpUserId = Number.parseInt(wpRaw, 10);
      const subIndex = subRaw ? Number.parseInt(subRaw, 10) : Number.NaN;
      if (!Number.isFinite(wpUserId) || wpUserId <= 0) return null;
      return {
        wpUserId,
        subIndex: Number.isFinite(subIndex) && subIndex > 0 ? subIndex : null,
      };
    }
    const wpUserId = Number.parseInt(raw, 10);
    if (!Number.isFinite(wpUserId) || wpUserId <= 0) return null;
    return { wpUserId, subIndex: null };
  };

  const buildPersonRecipientFromTrial = (trial: TrialLessonDTO): MessageRecipient | null => {
    const parsed = parseSubprofileRef(trial.subprofileId);
    if (!parsed) return null;
    return {
      id: `person:${parsed.wpUserId}:${parsed.subIndex ?? ''}`,
      type: 'person',
      wpUserId: parsed.wpUserId,
      subIndex: parsed.subIndex,
      name: trial.studentName || `Teilnehmer ${parsed.wpUserId}`,
    };
  };

  const resolvePersonFromCourse = (course: EnhancedCourse | null): { wpUserId: number; subIndex: number | null } | null => {
    if (!course) return null;
    const firstParticipant = Array.isArray(course.participants) && course.participants.length > 0
      ? course.participants[0]
      : null;
    if (firstParticipant?.subprofileId) {
      return parseSubprofileRef(firstParticipant.subprofileId);
    }
    if (firstParticipant && Number.isFinite(firstParticipant.wpUserId) && Number(firstParticipant.wpUserId) > 0) {
      const wpUserId = Number(firstParticipant.wpUserId);
      const subIndex = Number.isFinite(firstParticipant.subIndex) && Number(firstParticipant.subIndex) > 0
        ? Number(firstParticipant.subIndex)
        : null;
      return { wpUserId, subIndex };
    }
    return null;
  };
  // Handle menu item clicks
  const handleMenuItemClick = (action: string, participantName?: string) => {
    const {
      type,
      data,
      day,
      hour,
      minute
    } = contextMenu;
    switch (action) {
      case 'teacher_change': {
        if (type !== 'course' || !data) {
          closeContextMenu();
          return;
        }
        const seed = toTeacherChangeSeed(data as EnhancedCourse);
        if (!seed) {
          closeContextMenu();
          return;
        }
        setTeacherChangeSeed(seed);
        setTeacherChangeOpen(true);
        closeContextMenu();
        break;
      }
      // 'move_here' removed — drag-and-drop now opens the reschedule wizard directly
      case 'cancel':
        if (type === 'course') {
          setCancelCourseSeed(data as CourseCancelSeed);
          setCancelCourseOpen(true);
        } else if (type === 'trial') {
          setCancelTrialSeed(data as TrialLessonDTO);
          setCancelTrialOpen(true);
        }
        break;
      case 'reschedule':
        if (type === 'course') {
          const rc = data as EnhancedCourse;
          setRescheduleCourseSeed({
            id: rc.id,
            courseId: rc.courseId,
            ruleId: null,
            name: rc.name,
            fachName: rc.fachName,
            studentName: rc.studentName,
            studentEmail: rc.studentEmail,
            teacherId: rc.teacherId,
            roomId: rc.roomId,
            dayOfWeek: rc.dayOfWeek,
            startTime: rc.startTime,
            endTime: rc.endTime,
            startDate: rc.startDate || format(new Date(), 'yyyy-MM-dd'),
            sourceType: rc.sourceType,
            participants: rc.participants,
            dropTarget: null, // No drop target — wizard shows Step 2
          });
          setRescheduleCourseOpen(true);
        } else if (type === 'trial') {
          const tr = data as TrialLessonDTO;
          setRescheduleTrialSeed(tr);
          setRescheduleTrialTarget(null);
          setRescheduleTrialOpen(true);
        }
        break;
      case 'bulk_toggle':
        if (type === 'course' && data) {
          toggleBulkCourse((data as EnhancedCourse).id);
          setBulkMode(true);
        }
        break;
      case 'viewPerson':
        // Navigate to person
        if (type === 'trial') {
          const parsed = parseSubprofileRef((data as TrialLessonDTO | null)?.subprofileId);
          if (!parsed) {
            toast.error('Teilnehmer konnte nicht zugeordnet werden');
            break;
          }
          if (parsed.subIndex !== null && parsed.subIndex > 0) {
            globalThis.location.href = `/dashboard/people/${parsed.wpUserId}/sub/${parsed.wpUserId}-${parsed.subIndex}`;
          } else {
            globalThis.location.href = `/dashboard/people/${parsed.wpUserId}`;
          }
        } else {
          const course = data as EnhancedCourse | null;
          const parsed = resolvePersonFromCourse(course);
          if (!parsed) {
            toast.error('Teilnehmer konnte nicht zugeordnet werden');
            break;
          }
          if (parsed.subIndex !== null && parsed.subIndex > 0) {
            globalThis.location.href = `/dashboard/people/${parsed.wpUserId}/sub/${parsed.wpUserId}-${parsed.subIndex}`;
          } else {
            globalThis.location.href = `/dashboard/people/${parsed.wpUserId}`;
          }
        }
        break;
      case 'contactParticipants':
        if (type === 'course') {
          const course = data as EnhancedCourse;
          const recipients: MessageRecipient[] = (course.participants || []).map((participant) => ({
            id: `person:${participant.wpUserId}:${participant.subIndex || ''}`,
            type: 'person' as const,
            wpUserId: participant.wpUserId,
            subIndex: participant.subIndex || null,
            name: participant.name,
          }));
          if (recipients.length === 0) {
            toast.error('Keine eindeutig zugeordneten Teilnehmer vorhanden');
            break;
          }
          setMessageRecipients(recipients);
          setMessageOpen(true);
        } else if (type === 'trial') {
          const trial = data as TrialLessonDTO;
          const recipient = buildPersonRecipientFromTrial(trial);
          if (!recipient) {
            toast.error('Teilnehmer konnte nicht zugeordnet werden');
            break;
          }
          setMessageRecipients([recipient]);
          setMessageOpen(true);
        }
        break;
      case 'contactSingle':
        if (participantName && type === 'course') {
          const participant = (data as EnhancedCourse).participants?.find(
            (item) => item.name === participantName,
          );
          if (!participant) {
            toast.error('Teilnehmer konnte nicht eindeutig zugeordnet werden');
            break;
          }
          setMessageRecipients([{
            id: `person:${participant.wpUserId}:${participant.subIndex || ''}`,
            type: 'person',
            wpUserId: participant.wpUserId,
            subIndex: participant.subIndex || null,
            name: participant.name,
          }]);
          setMessageOpen(true);
        }
        break;
      case 'viewParticipant':
        // Navigate to participant's data record
        if (participantName && type === 'course' && data) {
          const course = data as EnhancedCourse;
          const participant = course.participants?.find(p => p.name === participantName);
          if (participant?.subprofileId) {
            const parsed = parseSubprofileRef(participant.subprofileId);
            if (parsed) {
              globalThis.location.href = parsed.subIndex !== null
                ? `/dashboard/people/${parsed.wpUserId}/sub/${parsed.wpUserId}-${parsed.subIndex}`
                : `/dashboard/people/${parsed.wpUserId}`;
              break;
            }
          }
          toast.info(`Teilnehmer-Profil konnte nicht aufgelöst werden`);
        }
        break;
      case 'terminateParticipant':
        // Open Kündigung wizard
        if (participantName && type === 'course' && data) {
          const course = data as EnhancedCourse;
          const participant = course.participants?.find(p => p.name === participantName);
          if (participant && course.courseId) {
            lookupTeilnahmeId(course.courseId, participant.subprofileId)
              .then((tid) => {
                setKuendigungTeilnahmeId(tid);
                setKuendigungWizardOpen(true);
              })
              .catch(() => {
                toast.error('Keine aktive Kursteilnahme für diesen Teilnehmer gefunden');
              });
          } else {
            toast.error('Keine aktive Kursteilnahme für diesen Teilnehmer gefunden');
          }
        }
        break;
      case 'changeCourse':
        toast.info('Ein Kurswechsel wird über Kündigung und anschließende Anmeldung durchgeführt.');
        break;
      case 'createTrial': {
        const timeString = minute !== undefined ? `${hour?.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}` : hour ? `${hour}:00` : '09:00';
        const dateStr = (activeView === 'calendar' && day) ? format(day, 'yyyy-MM-dd') : undefined;
        // Wenn day vorhanden, dayOfWeek berechnen (1-7, Montag=1)
        const dayOfWeek = day ? ((day.getDay() + 6) % 7) + 1 : undefined;
        const trialPrefillPayload = {
          teacherId: selectedTeacher,
          date: dateStr,
          startTime: timeString,
          dayOfWeek,
          requireDateSelection: activeView !== 'calendar',
        };
        closeContextMenu();
        // Availability-Check nur wenn Lehrer ausgewählt ist — sonst entscheidet der Wizard.
        if (selectedTeacher) {
          const pseudoDay = day || new Date();
          const outsideAvailability = !isWithinTeacherAvailabilityRange(pseudoDay, timeString, timeString);
          if (outsideAvailability) {
            const labelDate = day ? format(day, 'yyyy-MM-dd') : 'dem gewählten Tag';
            requestAvailabilityOverrideConfirm(
              `Die Startzeit ${timeString} liegt am ${labelDate} außerhalb der freigegebenen Lehrerzeiten. Hast du das wirklich mit dem Lehrer abgesprochen?`,
              { type: 'create_trial', prefill: trialPrefillPayload }
            );
            return;
          }
        }
        setTrialPrefill(trialPrefillPayload);
        setTrialDialogOpen(true);
        return;
      }
      case 'createCourse': {
        const courseTimeString = minute !== undefined ? `${hour?.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}` : hour ? `${hour}:00` : '09:00';
        const courseItem = {
          dayOfWeek: day ? ((day.getDay() + 6) % 7) + 1 : 1,
          hour: courseTimeString,
          teacherId: selectedTeacher
        };
        {
          const pseudoDay = day || new Date();
          const outsideAvailability = !isWithinTeacherAvailabilityRange(pseudoDay, courseTimeString, courseTimeString);
          if (outsideAvailability) {
            const labelDate = day ? format(day, 'yyyy-MM-dd') : 'dem gewählten Tag';
            requestAvailabilityOverrideConfirm(
              `Die Startzeit ${courseTimeString} liegt am ${labelDate} außerhalb der freigegebenen Lehrerzeiten. Hast du das wirklich mit dem Lehrer abgesprochen?`,
              { type: 'create_course', item: courseItem }
            );
          } else {
            setSelectedItem(courseItem);
            setCreateDialogOpen(true);
          }
        }
        break;
      }
    }
    closeContextMenu();
  };
  // Refresh both schedule views after wizard completes
  const handleWizardDone = () => {
    refetchSchedule();
    timetableHookData.refetch();
  };
  const handleAvailabilityConfirmProceed = () => {
    const action = availabilityConfirmAction;
    if (!action) {
      setAvailabilityConfirmOpen(false);
      return;
    }
    if (action.type === 'reschedule_drop') {
      setRescheduleCourseSeed(action.seed);
      setRescheduleCourseOpen(true);
    } else if (action.type === 'trial_reschedule_drop') {
      setRescheduleTrialSeed(action.trial);
      setRescheduleTrialTarget(action.target);
      setRescheduleTrialOpen(true);
    } else if (action.type === 'create_trial') {
      setTrialPrefill(action.prefill);
      setTrialDialogOpen(true);
    } else if (action.type === 'create_course') {
      setSelectedItem(action.item);
      setCreateDialogOpen(true);
    }
    setAvailabilityConfirmOpen(false);
    setAvailabilityConfirmAction(null);
    setAvailabilityConfirmMessage('');
  };
  const renderAvailabilityConfirmDialog = () => {
    return <Dialog
        open={availabilityConfirmOpen}
        onOpenChange={(open) => {
          setAvailabilityConfirmOpen(open);
          if (!open) {
            setAvailabilityConfirmAction(null);
            setAvailabilityConfirmMessage('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Außerhalb der Lehrer-Freigabe</DialogTitle>
            <DialogDescription>{availabilityConfirmMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAvailabilityConfirmOpen(false);
                setAvailabilityConfirmAction(null);
                setAvailabilityConfirmMessage('');
              }}
            >
              Abbrechen
            </Button>
            <Button onClick={handleAvailabilityConfirmProceed}>
              Ja, trotzdem fortfahren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>;
  };
  // Render the create dialog
  const renderCreateDialog = () => {
    return (
      <CreateCourseDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        selectedTeacher={selectedTeacher}
        teacherLabel={teacherLabel}
        selectedStandortId={selectedStandortId}
        currentWeek={currentWeek}
        selectedItem={selectedItem as { dayOfWeek?: number; hour?: string; teacherId?: string | number } | null}
        onCreated={() => {
          void refetchSchedule();
          void timetableHookData.refetch();
        }}
      />
    );
  };
  const normalizeTime = (time: string) => (time || '').slice(0, 5);
  const addMinutesToHHMM = (time: string, minutes: number): string => {
    const [h, m] = normalizeTime(time).split(':').map(Number);
    const total = (h || 0) * 60 + (m || 0) + minutes;
    const hh = ((Math.floor(total / 60) % 24) + 24) % 24;
    const mm = ((total % 60) + 60) % 60;
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  };
  
  // Konvertiere Timetable Events zu EnhancedCourse Format für Stundenplanansicht
  const scheduleCourses = useMemo(() => {
    // Filtere Unit-Events und Pattern-Events
    const unitEvents = timetableHookData.events.filter(e => e.type === 'unit');
    const patternEvents = timetableHookData.events.filter(e => e.type === 'pattern');
    const migrationEvents = timetableHookData.events.filter(e => e.type === 'migration');
    
    // Konvertiere Unit-Events
    const unitCourses = unitEvents.map(event => {
      // Parse date für dayOfWeek
      const eventDate = parseISO(event.date);
      const dayOfWeek = ((eventDate.getDay() + 6) % 7) + 1;
      
      // Finde die passende Unit für mehr Details
      // Über die ID, nicht über Datum und Uhrzeit: Zwei Einheiten zur selben Zeit
      // bekamen sonst den Status der jeweils anderen.
      const eventUnitId = unitIdOf(event.id);
      const unit = timetableHookData.units.find(u => u.id === eventUnitId);
      
      return {
        id: event.id,
        courseId: unit?.course_id || null,
        teacherId: String(selectedTeacher || ''),
        name: event.title || 'Unterricht',
        fachName: event.fachName || null,
        raumName: event.roomName || null,
        standortName: null,
        standortAdresse: null,
        teilnehmer: event.participants || null,
        // Der Kalender liefert die Teilnehmer an der Einheit, der Stundenplan am Muster.
        participants:
          (unit && 'teilnehmer' in unit ? (unit as { teilnehmer?: unknown }).teilnehmer : null)
          ?? (event as { participantList?: unknown }).participantList
          ?? null,
        dayOfWeek: dayOfWeek,
        startTime: event.startTime,
        endTime: event.endTime,
        startDate: event.date,
        status: unit?.status || 'aktiv',
        sourceType: unit?.source_type || null,
        isPlaceholder: Number((unit as any)?.kurs_is_placeholder ?? (event as any)?.isPlaceholder ?? 0) === 1,
        color: event.color,
        studentName: event.participants || '',
        studentEmail: ''
      } as EnhancedCourse;
    });
    
    // Konvertiere Pattern-Events (wiederkehrende Kurse)
    const patternCourses = patternEvents.map(event => {
      return {
        id: event.id,
        courseId: (event as any).courseId ?? null,
        teacherId: String(selectedTeacher || ''),
        name: event.title || 'Kurs',
        fachName: event.fachName || null,
        raumName: event.roomName || null,
        standortName: null,
        standortAdresse: null,
        teilnehmer: event.participants || null,
        // Teilnehmer als Datensätze: daran hängen „Zum Datensatz", „Kontaktieren"
        // und der Einstieg in die Kündigung.
        participants: event.participantList ?? null,
        dayOfWeek: event.dayOfWeek,
        startTime: event.startTime,
        endTime: event.endTime,
        startDate: event.date,
        // Hint vom useTeacherTimetable durchreichen ("Start ab 04.06." /
        // "Bis zum 31.07."). Wird in CourseBlock als Badge angezeigt.
        hint: event.hint ?? null,
        status: 'aktiv',
        sourceType: 'pattern',
        isPlaceholder: Boolean((event as any).isPlaceholder),
        color: event.color,
        studentName: event.participants || '',
        studentEmail: ''
      } as EnhancedCourse;
    });
    
    const migrationCourses = migrationEvents.map(event => ({
      id: event.id,
      courseId: null,
      teacherId: String(selectedTeacher || ''),
      name: event.title || 'Migration',
      fachName: event.fachName || null,
      raumName: null,
      standortName: null,
      standortAdresse: null,
      teilnehmer: event.participants || null,
      dayOfWeek: event.dayOfWeek,
      startTime: event.startTime,
      endTime: event.endTime,
      startDate: event.date,
      hint: event.hint ?? null,
      status: 'aktiv',
      sourceType: 'migration',
      isPlaceholder: true,
      color: event.color,
      studentName: event.participants || '',
      studentEmail: '',
    } as EnhancedCourse));

    return [...unitCourses, ...patternCourses, ...migrationCourses];
  }, [timetableHookData.events, timetableHookData.units, selectedTeacher]);
  const calculateTimePosition = (time: string) => {
    return timeToTopPx(normalizeTime(time), timeGridCfg);
  };
  const calculateDuration = (startTime: string, endTime: string) => {
    return durationToHeightPx(normalizeTime(startTime), normalizeTime(endTime), timeGridCfg);
  };
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const matchesSearch = useCallback((values: Array<string | null | undefined>) => {
    if (!normalizedSearch) return true;
    return values.some((value) => (value || '').toLowerCase().includes(normalizedSearch));
  }, [normalizedSearch]);
  const hasAnyStatusEnabled = Object.values(activeStatuses).some(Boolean);
  let minimumParticipants = 0;
  if (groupParticipantFilter === '10plus') {
    minimumParticipants = 10;
  } else if (groupParticipantFilter === '6plus') {
    minimumParticipants = 6;
  }
  const coursePassesFilters = useCallback((course: EnhancedCourse) => {
    if (!showExternalBlocked && course.isExternalBlocked) return false;
    const statusKey = ((course.status || 'aktiv') as ScheduleStatus);
    if (hasAnyStatusEnabled && !(activeStatuses[statusKey] ?? true)) return false;
    const participantCount = getParticipantCount(course);
    if (showOnlyGroupCourses && participantCount < GROUP_COURSE_THRESHOLD) return false;
    if (minimumParticipants > 0 && participantCount < minimumParticipants) return false;
    return matchesSearch([course.name, course.fachName, course.teilnehmer, course.raumName, course.studentName]);
  }, [showExternalBlocked, hasAnyStatusEnabled, activeStatuses, showOnlyGroupCourses, minimumParticipants, matchesSearch]);
  const trialPassesFilters = useCallback((trial: TrialLessonDTO) => {
    if (!showTrials || !stehtImTerminplan(trial)) return false;
    return matchesSearch([trial.studentName, trial.fachName, trial.instrument, trial.roomName, trial.status]);
  }, [showTrials, matchesSearch]);
  const filteredTeacherCourses = useMemo(() => teacherCourses.filter(coursePassesFilters), [teacherCourses, coursePassesFilters]);
  const filteredScheduleCourses = useMemo(() => scheduleCourses.filter(coursePassesFilters), [scheduleCourses, coursePassesFilters]);
  const filteredCalendarTrials = useMemo(() => calendarTrials.filter(trialPassesFilters), [calendarTrials, trialPassesFilters]);
  const filteredScheduleTrials = useMemo(() => scheduleTrials.filter(trialPassesFilters), [scheduleTrials, trialPassesFilters]);
  const timeGridCfg = useMemo(() => deriveTimeGridConfig(timeSlots as Array<{ time: string }>, rowPx), [timeSlots, rowPx]);
  const bulkSourceCourses = activeView === 'scheduler' ? filteredScheduleCourses : filteredTeacherCourses;
  const selectedBulkCourses = useMemo(
    () => bulkSourceCourses.filter((c) => bulkSelectedCourseIds.includes(String(c.id))),
    [bulkSourceCourses, bulkSelectedCourseIds]
  );
  const selectedBulkCount = selectedBulkCourses.length;
  const bulkVisibleIds = useMemo(() => bulkSourceCourses.map((c) => String(c.id)), [bulkSourceCourses]);
  const bulkVisibleIdSet = useMemo(() => new Set(bulkVisibleIds), [bulkVisibleIds]);
  const hasMatchingCourse = (trial: TrialLessonDTO, courses: EnhancedCourse[], dayIndex: number) => {
    if (!trial.courseId) return false;
    const trialStart = normalizeTime(trial.startTime);
    const trialEnd = normalizeTime(trial.endTime);
    return courses.some((course) =>
      course.dayOfWeek === dayIndex + 1 &&
      course.courseId === trial.courseId &&
      normalizeTime(course.startTime) === trialStart &&
      normalizeTime(course.endTime) === trialEnd
    );
  };
  /**
   * Kurse und freistehende Probestunden eines Tages gemeinsam anordnen.
   *
   * Beide Sorten liegen in derselben Tagesspalte. Rechnete jede für sich, bekamen
   * beide Spalte 0 von 1 und lagen aufeinander — im Seed-Standort stand die
   * Probestunde am Dienstag genau auf dem Kurs.
   */
  const buildDayLayout = (courses: EnhancedCourse[], dayIndex: number, dayTrials: TrialLessonDTO[]) =>
    layoutDayEntries([
      ...courses
        .filter((course) => course.dayOfWeek === dayIndex + 1)
        .map((course) => ({
          key: dayEntryKey.kurs(String(course.id)),
          startTime: normalizeTime(course.startTime),
          endTime: normalizeTime(course.endTime),
        })),
      ...dayTrials
        .filter((trial) => !hasMatchingCourse(trial, courses, dayIndex))
        .map((trial) => ({
          key: dayEntryKey.probestunde(String(trial.id)),
          startTime: normalizeTime(trial.startTime),
          endTime: normalizeTime(trial.endTime),
        })),
    ]);

  const renderFloatingTrials = (
    dayTrials: TrialLessonDTO[],
    courses: EnhancedCourse[],
    dayIndex: number,
    showDateLabel: boolean,
    dayLayout?: Map<string, { column: number; columns: number }>,
  ) => {
    const floatingTrials = dayTrials
      .filter((trial) => !hasMatchingCourse(trial, courses, dayIndex))
      .map((trial) => ({ ...trial, id: String(trial.id) }));

    const angeordnet = dayLayout
      ? floatingTrials.map((trial) => ({
          ...trial,
          ...(dayLayout.get(dayEntryKey.probestunde(trial.id)) ?? { column: 0, columns: 1 }),
        }))
      : layoutOverlappingEvents(floatingTrials);

    return angeordnet.map((trial) => {
      const startNorm = normalizeTime(trial.startTime);
      const endNorm = normalizeTime(trial.endTime);
      const fallbackEnd = durationToHeightPx(startNorm, endNorm, timeGridCfg) > 0
        ? endNorm
        : addMinutesToHHMM(startNorm, 45);
      const top = calculateTimePosition(startNorm);
      const height = calculateDuration(startNorm, fallbackEnd);
      const colStyle = overlapLayoutToColumnStyle(trial);
      const isNarrow = Number((trial as { columns?: number })?.columns ?? 1) > 1;
      const isTiny = height < 24;
      const isCompact = isNarrow || height < 42;
      const showDateInCard = showDateLabel && !isNarrow && height >= 64;
      const dateLabel = format(parseISO(trial.date), 'dd.MM.yyyy');
      const fachName = trial.fachName || trial.instrument || 'Probestunde';
      const tooltipContent = `Probestunde: ${trial.studentName}\nDatum: ${dateLabel}\nZeit: ${startNorm} - ${fallbackEnd}`;
      return (
        <button
          type="button"
          key={`floating-trial-${trial.id}`}
          className={`absolute block w-full appearance-none overflow-hidden rounded-md border p-1 text-left text-xs ${trial.courseId ? 'bg-yellow-200/90 border-yellow-400 text-yellow-950' : 'bg-yellow-100/80 border-yellow-300 text-yellow-900'} cursor-context-menu z-10 hover:opacity-80 transition-opacity`}
          style={{ top: `${top}px`, height: `${height}px`, ...colStyle }}
          draggable
          onDragStart={(e) => onTrialDragStart(e, trial as TrialLessonDTO)}
          onDragEnd={onTrialDragEnd}
          onContextMenu={(e) => handleContextMenu(e, 'trial', trial)}
          title={tooltipContent}
        >
          {!isTiny ? <div className="font-medium truncate whitespace-nowrap">{startNorm} | {fachName}</div> : null}
          {showDateInCard ? <div className="mt-0.5 truncate text-xs font-semibold whitespace-nowrap">{dateLabel}</div> : null}
          {!isCompact ? <div className="truncate border-t mt-1 pt-1 whitespace-nowrap">{trial.studentName}</div> : null}
        </button>
      );
    });
  };

  const getCalendarDayTrials = (day: Date) => filteredCalendarTrials.filter((trial) => isTrialOnDay(trial, day));
  const getSchedulerDayTrials = (_day: Date, dayIndex: number) =>
    filteredScheduleTrials.filter((trial) => {
      const trialDate = parseISO(trial.date);
      const trialDayOfWeek = ((trialDate.getDay() + 6) % 7) + 1;
      return trialDayOfWeek === dayIndex + 1;
    });
  const activeDropConflictStatus = useMemo(() => {
    if (!dropConflictPreview || !activeDropTarget) return null;
    if (
      dropConflictPreview.dayKey === activeDropTarget.dayKey
      && dropConflictPreview.hour === activeDropTarget.hour
      && dropConflictPreview.minute === activeDropTarget.minute
    ) {
      return dropConflictPreview.status;
    }
    return null;
  }, [dropConflictPreview, activeDropTarget]);

  const toggleBulkCourse = useCallback((courseId: string | number) => {
    const id = String(courseId);
    setBulkSelectedCourseIds((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  }, []);

  const openBulkMessageComposer = useCallback(() => {
    const standort = selectedStandortId ? Number(selectedStandortId) : 0;
    if (!standort) {
      toast.error('Kein Standort ausgewählt');
      return;
    }

    const unique = new Map<string, MessageRecipient>();
    selectedBulkCourses.forEach((course) => {
      const participants = Array.isArray(course.participants) ? course.participants : [];
      participants.forEach((p) => {
        if (!p?.wpUserId || !Number.isFinite(Number(p.subIndex))) return;
        const key = `${p.wpUserId}:${p.subIndex}`;
        if (!unique.has(key)) {
          unique.set(key, {
            id: `person:${p.wpUserId}:${p.subIndex}`,
            type: 'person',
            wpUserId: p.wpUserId,
            subIndex: p.subIndex,
            name: p.name || `Teilnehmer ${p.wpUserId}`,
          });
        }
      });
    });

    const recipients = Array.from(unique.values());
    if (recipients.length === 0) {
      toast.error('Keine strukturierten Teilnehmer in der Auswahl gefunden');
      return;
    }

    setMessageRecipients(recipients);
    setMessageOpen(true);
  }, [selectedStandortId, selectedBulkCourses]);

  // Status für Einträge setzen: Einheiten direkt, Mustertermine über den Kurs —
  // das Backend legt die Einheit dann an. Vorher übersprang der Sammelmodus alle
  // Kurstermine und im Stundenplan auch jede Einheit (ID „unit-123“) (T-0083).
  const applyStatus = useCallback(async (courses: EnhancedCourse[], status: BulkLessonStatus) => {
    const calls: Array<Promise<unknown>> = [];
    let skipped = 0;
    for (const course of courses) {
      const unitId = unitIdOf(course.id);
      if (unitId !== null) {
        calls.push(unterrichtseinheitenApi.update(unitId, { status }));
      } else if (course.sourceType === 'pattern' && course.courseId && course.startDate) {
        calls.push(unterrichtseinheitenApi.setOccurrenceStatus(Number(course.courseId), course.startDate, status));
      } else {
        skipped += 1;
      }
    }
    const results = await Promise.allSettled(calls);
    const okCount = results.filter((r) => r.status === 'fulfilled').length;
    if (okCount > 0) {
      refetchSchedule();
      timetableHookData.refetch();
      refetchCalendarTrials();
    }
    return { okCount, failedCount: results.length - okCount, skipped };
  }, [refetchSchedule, timetableHookData, refetchCalendarTrials]);

  const applyBulkStatus = useCallback(async (status: BulkLessonStatus) => {
    if (selectedBulkCourses.length === 0) {
      toast.error('Keine Kurse in der Sammelauswahl');
      return;
    }
    setBulkUpdatingStatus(status);
    try {
      const { okCount, failedCount, skipped } = await applyStatus(selectedBulkCourses, status);
      if (okCount === 0 && failedCount === 0) {
        toast.error('Keine aktualisierbaren Termine in der Auswahl gefunden');
        return;
      }
      const statusLabel = BULK_STATUS_DONE_LABEL[status];
      if (failedCount === 0 && skipped === 0) {
        toast.success(`${okCount} Termine wurden ${statusLabel}.`);
      } else {
        const skippedText = skipped > 0 ? `, ${skipped} übersprungen` : '';
        toast.message(
          `${okCount} aktualisiert, ${failedCount} fehlgeschlagen${skippedText}.`
        );
      }
      setBulkSelectedCourseIds([]);
    } catch {
      toast.error('Statusänderung der Sammelauswahl fehlgeschlagen');
    } finally {
      setBulkUpdatingStatus(null);
    }
  }, [selectedBulkCourses, applyStatus]);

  // Ein einzelner Termin über das Kontextmenü (T-0084).
  const applySingleStatus = useCallback(async (course: EnhancedCourse, status: BulkLessonStatus) => {
    closeContextMenu();
    try {
      const { okCount } = await applyStatus([course], status);
      if (okCount > 0) toast.success(`Termin wurde ${BULK_STATUS_DONE_LABEL[status]}.`);
      else toast.error('Der Termin konnte nicht geändert werden.');
    } catch {
      toast.error('Der Termin konnte nicht geändert werden.');
    }
  }, [applyStatus]);

  const selectAllVisibleForBulk = useCallback(() => {
    setBulkSelectedCourseIds((prev) => {
      const merged = new Set([...prev, ...bulkVisibleIds]);
      return Array.from(merged);
    });
  }, [bulkVisibleIds]);

  const invertVisibleBulkSelection = useCallback(() => {
    setBulkSelectedCourseIds((prev) => {
      const prevSet = new Set(prev);
      const nextVisible = bulkVisibleIds.filter((id) => !prevSet.has(id));
      const keepHiddenSelections = prev.filter((id) => !bulkVisibleIdSet.has(id));
      return [...keepHiddenSelections, ...nextVisible];
    });
  }, [bulkVisibleIds, bulkVisibleIdSet]);

  useEffect(() => {
    setBulkSelectedCourseIds((prev) => prev.filter((id) => bulkVisibleIdSet.has(id)));
  }, [bulkVisibleIdSet]);

  useScheduleKeyboardShortcuts({
    activeView,
    setActiveView,
    bulkMode,
    setBulkMode,
    setBulkSelectedCourseIds,
    selectAllVisibleForBulk,
    nextWeek,
    previousWeek,
    setCreateDialogOpen,
    selectedTeacher,
    teacherLabel,
    setTrialPrefill,
    setTrialDialogOpen,
  });

  // Render the calendar view
  const renderCalendarView = () => {
    return <div className="space-y-4">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={previousWeek}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToCurrentWeek}>
              Heute
            </Button>
            <Button variant="outline" size="sm" onClick={nextWeek}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="text-xs font-medium text-muted-foreground md:text-sm md:text-foreground">
            <span className="md:hidden">
              {format(calendarWeekDays[0], 'dd.MM.', { locale: de })} - {format(calendarWeekDays[6], 'dd.MM.yyyy', { locale: de })}
            </span>
            <span className="hidden md:inline">
              {format(calendarWeekDays[0], 'd. MMMM yyyy', { locale: de })} - {format(calendarWeekDays[6], 'd. MMMM yyyy', { locale: de })}
            </span>
          </div>
        </div>
        <FullScheduleGrid
          days={calendarWeekDays}
          timeSlots={timeSlots}
          courses={filteredTeacherCourses}
          rowPx={rowPx}
          gridMaxHeight={gridMaxHeight}
          calcTop={calculateTimePosition}
          calcHeight={calculateDuration}
          onContextMenuSlot={(e, d, hour, minute) => handleContextMenu(e, 'empty', null, d, hour, minute)}
          onContextMenuCourse={(e, course) => handleContextMenu(e, 'course', course)}
          onClickCourse={(course) => openCourseDetails(course)}
          onDragOverSlot={handleSlotDragOver}
          onDropSlot={handleSlotDrop}
          isDraggingCourse={!!draggedCourse || !!draggedTrial}
          activeDropTarget={activeDropTarget}
          activeDropConflictStatus={activeDropConflictStatus}
          onCourseDragStart={onCourseDragStart}
          onCourseDragEnd={onCourseDragEnd}
          onTrialDragStart={onTrialDragStart}
          onTrialDragEnd={onTrialDragEnd}
          getTrialsForDay={(day) => getCalendarDayTrials(day)}
          onContextMenuTrial={(e, trial) => handleContextMenu(e, 'trial', trial)}
          getDayLayout={(dayIndex, dayTrials) => buildDayLayout(filteredTeacherCourses, dayIndex, dayTrials)}
          renderFloatingTrials={(_day, dayIndex, dayTrials, dayLayout) => renderFloatingTrials(dayTrials, filteredTeacherCourses, dayIndex, true, dayLayout)}
          availabilitySlots={teacherAvailability}
          renderContextMenu={renderContextMenu}
          calendarRef={calendarRef}
        />
      </div>;
  };
  // Render the scheduler view (timetable)
  const renderSchedulerView = () => {
    return <div className="space-y-4">
        <Legend />
        <FullScheduleGrid
          days={scheduleWeekDays}
          timeSlots={timeSlots}
          courses={filteredScheduleCourses}
          rowPx={rowPx}
          gridMaxHeight={gridMaxHeight}
          calcTop={calculateTimePosition}
          calcHeight={calculateDuration}
          onContextMenuSlot={(e, day, hour, minute) => handleContextMenu(e, 'empty', null, day, hour, minute)}
          onContextMenuCourse={(e, course) => handleContextMenu(e, 'course', course)}
          onClickCourse={(course) => openCourseDetails(course)}
          onDragOverSlot={handleSlotDragOver}
          onDropSlot={handleSlotDrop}
          isDraggingCourse={!!draggedCourse || !!draggedTrial}
          activeDropTarget={activeDropTarget}
          activeDropConflictStatus={activeDropConflictStatus}
          onCourseDragStart={onCourseDragStart}
          onCourseDragEnd={onCourseDragEnd}
          onTrialDragStart={onTrialDragStart}
          onTrialDragEnd={onTrialDragEnd}
          getContingentColor={getContingentColor}
          getTrialsForDay={(day, dayIndex) => getSchedulerDayTrials(day, dayIndex)}
          onContextMenuTrial={(e, trial) => handleContextMenu(e, 'trial', trial)}
          getDayLayout={(dayIndex, dayTrials) => buildDayLayout(filteredScheduleCourses, dayIndex, dayTrials)}
          renderFloatingTrials={(_day, dayIndex, dayTrials, dayLayout) => renderFloatingTrials(dayTrials, filteredScheduleCourses, dayIndex, false, dayLayout)}
          availabilitySlots={teacherAvailability}
          renderContextMenu={renderContextMenu}
          calendarRef={calendarRef}
          showHeaderDates={false}
        />
      </div>;
  };
  const renderResourceView = () => {
    return <div className="space-y-4">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={previousWeek}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToCurrentWeek}>
              Heute
            </Button>
            <Button variant="outline" size="sm" onClick={nextWeek}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="text-xs font-medium text-muted-foreground md:text-sm md:text-foreground">
            {format(calendarWeekDays[0], 'd. MMMM yyyy', { locale: de })} - {format(calendarWeekDays[6], 'd. MMMM yyyy', { locale: de })}
          </div>
        </div>
        <ResourceMatrixView
          days={calendarWeekDays}
          courses={filteredTeacherCourses}
          trials={filteredCalendarTrials}
        />
      </div>;
  };
  // Render the context menu
  const renderContextMenu = () => {
    if (!contextMenu.isOpen) return null;
    const {
      position,
      type
    } = contextMenu;
    const actionClassName = 'w-full justify-start px-2';
    const menuWidth = 224; // w-56
    const menuHeightByType: Record<ContextMenuState['type'], number> = {
      course: 340,
      trial: 190,
      empty: 110,
      booking: 110,
      drop: 110,
    };
    const menuHeight = menuHeightByType[type] ?? 190;
    const bounds = calendarRef.current?.getBoundingClientRect();
    const margin = 8;
    const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
    const maxLeft = bounds ? Math.max(margin, bounds.width - menuWidth - margin) : position.x;
    const maxTop = bounds ? Math.max(margin, bounds.height - menuHeight - margin) : position.y;
    const menuLeft = clamp(position.x, margin, maxLeft);
    const menuTop = clamp(position.y, margin, maxTop);
    const participantSubmenuProps = {
      side: 'right' as const,
      align: 'start' as const,
      sideOffset: 6,
      collisionPadding: 16,
      avoidCollisions: true,
    };
    return <div className="absolute z-[110] w-56 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-surface motion-safe:animate-fade-up" style={{
      left: `${menuLeft}px`,
      top: `${menuTop}px`
    }}>
        {type === 'course' && (contextMenu.data as EnhancedCourse)?.isExternalBlocked && (
          <div className="py-1 px-2 text-xs text-muted-foreground">
            Externe Blockzeit - keine Details verfuegbar
          </div>
        )}
        {type === 'course' && !(contextMenu.data as EnhancedCourse)?.isExternalBlocked && <div className="py-1">
            <Button variant="ghost" size="sm" className={actionClassName} onClick={() => handleMenuItemClick('teacher_change')}>
              <User className="mr-2 h-4 w-4" />
              <span>Lehrerwechsel</span>
            </Button>
            <Button variant="ghost" size="sm" className={actionClassName} onClick={() => handleMenuItemClick('reschedule')}>
              <Calendar className="mr-2 h-4 w-4" />
              <span>Kurs verschieben</span>
            </Button>
            <Button variant="ghost" size="sm" className={actionClassName} onClick={() => handleMenuItemClick('cancel')}>
              <X className="mr-2 h-4 w-4" />
              <span>Termin absagen</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className={actionClassName}>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  <span>Status setzen</span>
                  <ChevronRight className="ml-auto h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="z-[120] min-w-[200px]" align="end" alignOffset={-5} sideOffset={-1}>
                <DropdownMenuItem onClick={() => void applySingleStatus(contextMenu.data as EnhancedCourse, 'durchgeführt')}>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  <span>Durchgeführt</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => void applySingleStatus(contextMenu.data as EnhancedCourse, 'nicht_erschienen')}>
                  <UserX className="mr-2 h-4 w-4" />
                  <span>Nicht erschienen</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => void applySingleStatus(contextMenu.data as EnhancedCourse, 'geplant')}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  <span>Zurück auf geplant</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="sm" className={actionClassName} onClick={() => handleMenuItemClick('viewPerson')}>
              <User className="mr-2 h-4 w-4" />
              <span>Zur Person springen</span>
            </Button>
            <Button variant="ghost" size="sm" className={actionClassName} onClick={() => handleMenuItemClick('contactParticipants')}>
              <Mail className="mr-2 h-4 w-4" />
              <span>Teilnehmer kontaktieren</span>
            </Button>
            <Button variant="ghost" size="sm" className={actionClassName} onClick={() => handleMenuItemClick('bulk_toggle')}>
              <Grid2x2 className="mr-2 h-4 w-4" />
              <span>
                {bulkSelectedCourseIds.includes(String((contextMenu.data as EnhancedCourse)?.id))
                  ? 'Aus Sammelauswahl entfernen'
                  : 'Zur Sammelauswahl hinzufügen'}
              </span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className={actionClassName}>
                  <User className="mr-2 h-4 w-4" />
                  <span>Teilnehmer</span>
                  <ChevronRight className="ml-auto h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="z-[120] min-w-[220px]" align="end" alignOffset={-5} sideOffset={-1}>
                {((contextMenu.data as EnhancedCourse).participants || []).map((participant) => (
                  <DropdownMenuSub key={`${participant.wpUserId}-${participant.subIndex}-${participant.name}`}>
                    <DropdownMenuSubTrigger>
                      <User className="mr-2 h-4 w-4" />
                      <span>{participant.name}</span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="z-[130] min-w-[180px]" {...participantSubmenuProps}>
                      <DropdownMenuItem onClick={() => handleMenuItemClick('viewParticipant', participant.name)}>
                        <Eye className="mr-2 h-4 w-4" />
                        <span>Zum Datensatz</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleMenuItemClick('terminateParticipant', participant.name)}>
                        <X className="mr-2 h-4 w-4" />
                        <span>Kündigung einrichten</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleMenuItemClick('contactSingle', participant.name)}>
                        <Mail className="mr-2 h-4 w-4" />
                        <span>Kontaktieren</span>
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                ))}
                {((contextMenu.data as EnhancedCourse).participants || []).length === 0 && (
                  <p className="px-2 py-1.5 text-xs text-muted-foreground">
                    Keine zugeordneten Teilnehmer
                  </p>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>}
        {/* Drop context menu removed — drag-and-drop now opens reschedule wizard directly */}
        {type === 'trial' && <div className="py-1">
            <Button variant="ghost" size="sm" className={actionClassName} onClick={() => handleMenuItemClick('reschedule')}>
              <Calendar className="mr-2 h-4 w-4" />
              <span>Probestunde verschieben</span>
            </Button>
            <Button variant="ghost" size="sm" className={actionClassName} onClick={() => handleMenuItemClick('cancel')}>
              <X className="mr-2 h-4 w-4" />
              <span>Probestunde absagen</span>
            </Button>
            <Button variant="ghost" size="sm" className={actionClassName} onClick={() => handleMenuItemClick('viewPerson')}>
              <User className="mr-2 h-4 w-4" />
              <span>Zur Person springen</span>
            </Button>
            <Button variant="ghost" size="sm" className={actionClassName} onClick={() => handleMenuItemClick('contactParticipants')}>
              <Mail className="mr-2 h-4 w-4" />
              <span>Teilnehmer kontaktieren</span>
            </Button>
          </div>}
        {type === 'empty' && <div className="py-1">
            <Button variant="ghost" size="sm" className={actionClassName} onClick={() => handleMenuItemClick('createTrial')}>
              <Plus className="mr-2 h-4 w-4" />
              <span>Probestunde erstellen</span>
            </Button>
            <Button variant="ghost" size="sm" className={actionClassName} onClick={() => handleMenuItemClick('createCourse')}>
              <Plus className="mr-2 h-4 w-4" />
              <span>Kurs erstellen</span>
            </Button>
          </div>}
      </div>;
  };
  // Render the contact dialog
  const renderContactDialog = () => {
    return <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Teilnehmer kontaktieren</DialogTitle>
            <DialogDescription>
              Wählen Sie die Kontaktmethode und Teilnehmer aus, die Sie
              kontaktieren möchten.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <label className="text-right text-sm font-medium">
                Kontaktmethode
              </label>
              <div className="col-span-3 flex space-x-4">
                <div className="flex items-center space-x-2">
                  <input type="radio" id="email" name="contactMethod" value="email" checked={contactMethod === 'email'} onChange={() => setContactMethod('email')} className="h-4 w-4" />
                  <label htmlFor="email" className="text-sm">
                    E-Mail
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <input type="radio" id="sms" name="contactMethod" value="sms" checked={contactMethod === 'sms'} onChange={() => setContactMethod('sms')} className="h-4 w-4" />
                  <label htmlFor="sms" className="text-sm">
                    SMS
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <input type="radio" id="whatsapp" name="contactMethod" value="whatsapp" checked={contactMethod === 'whatsapp'} onChange={() => setContactMethod('whatsapp')} className="h-4 w-4" />
                  <label htmlFor="whatsapp" className="text-sm">
                    WhatsApp
                  </label>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <label className="text-right text-sm font-medium mt-2">
                Teilnehmer
              </label>
              <div className="col-span-3 space-y-2 border rounded-md p-3">
                {selectedParticipants.map((participant, index) => <div key={index} className="flex items-center space-x-2">
                    <input type="checkbox" id={`participant-${index}`} checked={selectedParticipants.includes(participant)} onChange={e => {
                  if (e.target.checked) {
                    setSelectedParticipants(prev => [...prev, participant]);
                  } else {
                    setSelectedParticipants(prev => prev.filter(p => p !== participant));
                  }
                }} className="h-4 w-4" />
                    <label htmlFor={`participant-${index}`} className="text-sm">
                      {participant}
                    </label>
                  </div>)}
              </div>
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <label className="text-right text-sm font-medium mt-2">
                Nachricht
              </label>
              <Textarea className="col-span-3" rows={10} value={messageText} onChange={e => setMessageText(e.target.value)} placeholder="Geben Sie Ihre Nachricht ein..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={() => {
            toast.success(`Nachricht an ${selectedParticipants.length} Teilnehmer via ${contactMethod === 'email' ? 'E-Mail' : contactMethod === 'sms' ? 'SMS' : 'WhatsApp'} gesendet`);
            setContactDialogOpen(false);
          }}>
              <Send className="mr-2 h-4 w-4" />
              Senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>;
  };
  // Render the termination dialog
  const renderTerminateDialog = () => {
    return <Dialog open={terminateDialogOpen} onOpenChange={setTerminateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kündigung einrichten</DialogTitle>
            <DialogDescription>
              Richten Sie eine Kündigung für {selectedParticipant} ein.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <label className="text-right text-sm font-medium">
                Kündigungsdatum
              </label>
              <Input type="date" className="col-span-3" defaultValue={new Date().toISOString().split('T')[0]} />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label className="text-right text-sm font-medium">
                Kündigungsgrund
              </label>
              <Select defaultValue="personal">
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Grund auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal">Persönliche Gründe</SelectItem>
                  <SelectItem value="relocation">Umzug</SelectItem>
                  <SelectItem value="financial">Finanzielle Gründe</SelectItem>
                  <SelectItem value="dissatisfaction">
                    Unzufriedenheit
                  </SelectItem>
                  <SelectItem value="other">Sonstiges</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <label className="text-right text-sm font-medium mt-2">
                Anmerkungen
              </label>
              <Textarea className="col-span-3" rows={4} placeholder="Zusätzliche Informationen zur Kündigung..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTerminateDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={() => {
            toast.error('Die Kursteilnahme konnte nicht eindeutig zugeordnet werden.');
          }}>
              Kündigung bestätigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>;
  };
  // Render the change course dialog
  const renderChangeCourseDialog = () => {
    return <Dialog open={changeCourseDialogOpen} onOpenChange={setChangeCourseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kurs wechseln</DialogTitle>
            <DialogDescription>
              Wechseln Sie den Kurs für {selectedParticipant}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <label className="text-right text-sm font-medium">
                Aktueller Kurs
              </label>
              <div className="col-span-3 text-sm font-medium">
                {(contextMenu.data as EnhancedCourse | null)?.name || 'Nicht verfügbar'}
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label className="text-right text-sm font-medium">
                Neuer Kurs
              </label>
              <Select disabled>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Kurs auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unavailable">Keine Kursauswahl verfügbar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label className="text-right text-sm font-medium">
                Wechseldatum
              </label>
              <Input type="date" className="col-span-3" defaultValue={new Date().toISOString().split('T')[0]} />
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <label className="text-right text-sm font-medium mt-2">
                Grund für Wechsel
              </label>
              <Textarea className="col-span-3" rows={4} placeholder="Grund für den Kurswechsel..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeCourseDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={() => {
            toast.info('Ein Kurswechsel wird über Kündigung und neue Anmeldung durchgeführt.');
          }}>
              Wechsel bestätigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>;
  };
  return <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Terminübersicht</h1>
      </div>
      <ScheduleToolbar
        teacherLabel={teacherLabel}
        teacherOptions={teacherOptions}
        selectedTeacher={selectedTeacher}
        onTeacherChange={(teacherId) => setSelectedTeacher(teacherId)}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        showTrials={showTrials}
        onShowTrialsChange={setShowTrials}
        showExternalBlocked={showExternalBlocked}
        onShowExternalBlockedChange={setShowExternalBlocked}
        showOnlyGroupCourses={showOnlyGroupCourses}
        onShowOnlyGroupCoursesChange={setShowOnlyGroupCourses}
        groupParticipantFilter={groupParticipantFilter}
        onGroupParticipantFilterChange={setGroupParticipantFilter}
        activeStatuses={activeStatuses}
        onToggleStatus={(status) => setActiveStatuses((prev) => ({ ...prev, [status]: !prev[status] }))}
        onResetFilters={() => {
          setSearchQuery('');
          setShowTrials(true);
          setShowExternalBlocked(true);
          setShowOnlyGroupCourses(false);
          setGroupParticipantFilter('all');
          setActiveStatuses({
            aktiv: true,
            geplant: true,
            verschoben: true,
            abgesagt: true,
            durchgeführt: true,
            nicht_erschienen: true,
            abgeschlossen: true,
          });
        }}
      />
      <div className="rounded-md border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <Keyboard className="h-3.5 w-3.5" />
          Shortcuts:
        </span>{' '}
        <span className="font-medium">1</span> Kalender, <span className="font-medium">2</span> Stundenplan, <span className="font-medium">3</span> Ressourcen,{' '}
        <span className="font-medium">N</span> Kurs anlegen, <span className="font-medium">T</span> Probestunde,{' '}
        <span className="font-medium">B</span> Sammelauswahl, <span className="font-medium">Esc</span> Sammelauswahl leeren.
      </div>
      {bulkMode && (
        <div className="rounded-lg border bg-background p-3">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div className="text-sm">
              <span className="font-medium">Sammelauswahl:</span>{' '}
              <Badge variant="secondary">{selectedBulkCount} Kurse</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={selectAllVisibleForBulk}
                disabled={bulkVisibleIds.length === 0}
              >
                Alle sichtbaren
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={invertVisibleBulkSelection}
                disabled={bulkVisibleIds.length === 0}
              >
                Auswahl invertieren
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={openBulkMessageComposer}
                disabled={selectedBulkCount === 0}
              >
                <Mail className="mr-2 h-4 w-4" />
                Sammel-Nachricht
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => applyBulkStatus('durchgeführt')}
                disabled={selectedBulkCount === 0 || bulkUpdatingStatus !== null}
              >
                Als durchgeführt markieren
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => applyBulkStatus('nicht_erschienen')}
                disabled={selectedBulkCount === 0 || bulkUpdatingStatus !== null}
                title="Unentschuldigtes Fehlen – verbraucht wie eine durchgeführte Stunde eine Kontingent-Einheit"
              >
                Nicht erschienen
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => applyBulkStatus('abgesagt')}
                disabled={selectedBulkCount === 0 || bulkUpdatingStatus !== null}
              >
                Sammel-Absage
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setBulkSelectedCourseIds([])}>
                Auswahl leeren
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setBulkMode(false)}>
                Sammelauswahl beenden
              </Button>
            </div>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Kurse per Rechtsklick im Kontextmenü zur Sammelauswahl hinzufügen oder entfernen. Mit{' '}
            <span className="font-medium"> Ctrl/Cmd + A</span> in der Sammelauswahl werden alle sichtbaren Kurse ausgewählt.
          </p>
          {bulkUpdatingStatus && (
            <p className="mt-1 text-xs text-muted-foreground">
              Status wird gesetzt …
            </p>
          )}
          {selectedBulkCount > 0 && (
            <div className="mt-3 rounded-md border p-2">
              <div className="mb-2 text-xs font-medium text-muted-foreground">Ausgewählte Kurse</div>
              <div className="flex flex-wrap gap-2">
                {selectedBulkCourses.map((course) => (
                  <button
                    key={`bulk-pill-${course.id}`}
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full border bg-muted/40 px-2.5 py-1 text-xs hover:bg-muted"
                    onClick={() => toggleBulkCourse(course.id)}
                    title="Aus Auswahl entfernen"
                  >
                    <span className="max-w-[220px] truncate">{course.name || `Kurs ${course.id}`}</span>
                    <span className="text-muted-foreground">×</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {/* View tabs */}
      <Tabs defaultValue="scheduler" value={activeView} onValueChange={setActiveView}>
        <TabsList>
          <TabsTrigger value="calendar" className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            Kalenderansicht
          </TabsTrigger>
          <TabsTrigger value="scheduler" className="flex items-center gap-1">
            {/* Zeit-Icon innerhalb des Managers nicht mehr benötigt */}
            Stundenplanansicht
          </TabsTrigger>
          <TabsTrigger value="resources" className="flex items-center gap-1">
            <Grid2x2 className="h-4 w-4" />
            Ressourcenansicht
          </TabsTrigger>
        </TabsList>
        <TabsContent value="calendar" className="mt-4">
          <div className={!selectedTeacher ? 'pointer-events-none opacity-50' : ''}>
            {renderCalendarView()}
          </div>
        </TabsContent>
        <TabsContent value="scheduler" className="mt-4">
          <div className={!selectedTeacher ? 'pointer-events-none opacity-50' : ''}>
            {renderSchedulerView()}
          </div>
        </TabsContent>
        <TabsContent value="resources" className="mt-4">
          <div className={!selectedTeacher ? 'pointer-events-none opacity-50' : ''}>
            {renderResourceView()}
          </div>
        </TabsContent>
      </Tabs>
      {/* Probestunden-Assistent, Rückfrage „in den Kurs / zur gleichen Zeit“ und
          Rundmail auf oberster Ebene: Sie standen nur im Kalender-Tab, im
          Stundenplan öffneten Rechtsklick, Taste T und „Teilnehmer kontaktieren“
          deshalb nichts (PS-2.1, PS-2.4). */}
      <TrialWizardModal
        open={trialDialogOpen}
        onOpenChange={setTrialDialogOpen}
        defaultTeacherId={trialPrefill.teacherId}
        lockTeacher={true}
        defaultDate={trialPrefill.date}
        defaultStartTime={trialPrefill.startTime}
        defaultDayOfWeek={trialPrefill.dayOfWeek}
        defaultRoomId={trialPrefill.roomId}
        defaultCourseId={trialPrefill.courseId}
        requireDateSelection={trialPrefill.requireDateSelection}
        onCreated={() => { 
          refetchSchedule(); 
          timetableHookData.refetch();
          refetchCalendarTrials();
        }}
      />
      <Dialog
        open={trialCoursePickOpen}
        onOpenChange={(v) => {
          setTrialCoursePickOpen(v);
          if (!v) setTrialCoursePickEvent(null);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Probestunde anlegen</DialogTitle>
            <DialogDescription>
              Möchtest du die Probestunde <span className="font-medium">in diesen Kurs</span> legen oder nur <span className="font-medium">zur gleichen Zeit</span> eintragen?
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-md border p-3 text-sm">
            <div className="font-medium">{trialCoursePickEvent?.name || 'Kurs'}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {(trialCoursePickEvent?.startDate || '—')} • {(trialCoursePickEvent?.startTime || '—')}–{(trialCoursePickEvent?.endTime || '—')}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setTrialCoursePickOpen(false)}>
              Abbrechen
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={!trialCoursePickEvent}
              onClick={() => {
                const ev = trialCoursePickEvent;
                if (!ev) return;
                if (!selectedTeacher) { toast.error('Bitte zuerst einen ' + teacherLabel + ' auswählen'); return; }

                const dayOfWeek = ev.dayOfWeek ?? (() => {
                  const d = (ev.startDate || '').trim();
                  if (!d) return undefined;
                  const js = new Date(`${d}T00:00:00`).getDay();
                  return js === 0 ? 7 : js;
                })();

                const hasFixedDate = activeView === 'calendar' && !!ev.startDate;
                setTrialPrefill({
                  teacherId: selectedTeacher,
                  date: hasFixedDate ? ev.startDate : undefined,
                  startTime: ev.startTime,
                  dayOfWeek,
                  roomId: ev.roomId ?? undefined,
                  requireDateSelection: !hasFixedDate,
                  courseId: undefined, // gleiche Zeit, ohne Kurs
                });
                setTrialCoursePickOpen(false);
                setTrialDialogOpen(true);
              }}
            >
              Zur gleichen Zeit
            </Button>
            <Button
              type="button"
              disabled={!trialCoursePickEvent || !(trialCoursePickEvent?.courseId)}
              onClick={() => {
                const ev = trialCoursePickEvent;
                if (!ev) return;
                if (!selectedTeacher) { toast.error('Bitte zuerst einen ' + teacherLabel + ' auswählen'); return; }
                if (!ev.courseId) { toast.error('Kurs-ID fehlt'); return; }

                const dayOfWeek = ev.dayOfWeek ?? (() => {
                  const d = (ev.startDate || '').trim();
                  if (!d) return undefined;
                  const js = new Date(`${d}T00:00:00`).getDay();
                  return js === 0 ? 7 : js;
                })();

                const hasFixedDate = activeView === 'calendar' && !!ev.startDate;
                setTrialPrefill({
                  teacherId: selectedTeacher,
                  date: hasFixedDate ? ev.startDate : undefined,
                  startTime: ev.startTime,
                  dayOfWeek,
                  roomId: ev.roomId ?? undefined,
                  requireDateSelection: !hasFixedDate,
                  courseId: ev.courseId ?? undefined,
                });
                setTrialCoursePickOpen(false);
                setTrialDialogOpen(true);
              }}
            >
              In diesen Kurs
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <MessageWizardModal
        open={messageOpen}
        onOpenChange={setMessageOpen}
        standortId={selectedStandortId ? Number(selectedStandortId) : 0}
        title={messageTitle}
        recipients={messageRecipients}
        initialChannel={messageChannel}
        templateKeys={{
          email: 'communications.manual.course.participants',
          sms: 'communications.manual.course.participants',
        }}
        initialDrafts={{
          email: {
            subjectTpl: 'Information zu Ihrem Kurs',
            htmlTpl: '<p>Hallo {{recipientName}},</p><p></p><p>Viele Grüße<br>{{schoolName}}</p>',
            textTpl: 'Hallo {{recipientName}},\n\n\nViele Grüße\n{{schoolName}}',
          },
          sms: { smsTpl: 'Hallo {{recipientName}}, ' },
        }}
      />
      {/* Modal auf Top-Level, damit es auch sichtbar wird, wenn der Klick
          aus dem Stundenplan- oder Ressourcenview kommt (TabsContent rendert
          nur den aktiven Tab — vorher war der Wizard nur im Calendar-Tree). */}
      <CourseDetailsWizard
        open={courseDetailsOpen}
        onOpenChange={setCourseDetailsOpen}
        event={selectedCourseDetails}
        standortId={selectedStandortId ? Number(selectedStandortId) : undefined}
        onContactPerson={(kontakt, kanal) => {
          if (!selectedStandortId) {
            toast.error('Kein Standort ausgewählt');
            return;
          }
          setMessageRecipients([
            {
              id: `person:${kontakt.wpUserId}:${kontakt.subIndex ?? ''}`,
              type: 'person',
              wpUserId: kontakt.wpUserId,
              subIndex: kontakt.subIndex,
              name: kontakt.name,
            },
          ]);
          setMessageChannel(kanal);
          setMessageTitle(`Nachricht an ${kontakt.name}`);
          setMessageOpen(true);
        }}
        onJumpToTeacher={(lehrerId) => {
          globalThis.location.href = `/dashboard/people/${lehrerId}?tab=teacher`;
        }}
        onAddTrial={(ev) => {
          setTrialCoursePickEvent(ev);
          setTrialCoursePickOpen(true);
        }}
        onRescheduleCourse={(ev) => {
          // Existing behavior expects contextMenu.type='course'
          setContextMenu(prev => ({ ...prev, isOpen: false, type: 'course', data: ev as EnhancedCourse }));
          handleMenuItemClick('reschedule');
        }}
        onCancelCourse={(ev) => {
          setContextMenu(prev => ({ ...prev, isOpen: false, type: 'course', data: ev as EnhancedCourse }));
          handleMenuItemClick('cancel');
        }}
        onContactParticipants={(ev) => {
          const standort = selectedStandortId ? Number(selectedStandortId) : 0;
          if (!standort) {
            toast.error('Kein Standort ausgewählt');
            return;
          }
          const parts = Array.isArray(ev.participants) ? ev.participants : [];
          if (parts.length === 0) {
            toast.error('Keine strukturierten Teilnehmer vorhanden');
            return;
          }
          setMessageRecipients(
            parts.map((p) => ({
              id: `person:${p.wpUserId}:${p.subIndex}`,
              type: 'person',
              wpUserId: p.wpUserId,
              subIndex: p.subIndex,
              name: p.name,
            }))
          );
          setMessageOpen(true);
        }}
        onJumpToPerson={(p) => {
          // Route exists: /dashboard/people/:id/sub/:subId
          globalThis.location.href = `/dashboard/people/${p.wpUserId}/sub/${p.wpUserId}-${p.subIndex}`;
        }}
      />
      {/* Dialogs */}
      {renderAvailabilityConfirmDialog()}
      {cancelCourseSeed && <CourseCancelWizardModal open={cancelCourseOpen} onOpenChange={setCancelCourseOpen} course={cancelCourseSeed} onDone={handleWizardDone} />}
      {cancelTrialSeed && (
        <CancelTrialWizardModal
          open={cancelTrialOpen}
          onOpenChange={(open) => {
            setCancelTrialOpen(open);
            if (!open) {
              setCancelTrialSeed(null);
            }
          }}
          trial={cancelTrialSeed}
          onDone={async () => {
            setCancelTrialOpen(false);
            setCancelTrialSeed(null);
            await refetchSchedule();
            await timetableHookData.refetch();
            await refetchCalendarTrials();
          }}
        />
      )}
      {rescheduleCourseSeed && <CourseRescheduleWizardModal open={rescheduleCourseOpen} onOpenChange={setRescheduleCourseOpen} course={rescheduleCourseSeed} onDone={handleWizardDone} />}
      {rescheduleTrialSeed && (
        <RescheduleTrialModal
          open={rescheduleTrialOpen}
          onOpenChange={(open) => {
            setRescheduleTrialOpen(open);
            if (!open) {
              setRescheduleTrialSeed(null);
              setRescheduleTrialTarget(null);
            }
          }}
          trial={rescheduleTrialSeed}
          initialTarget={rescheduleTrialTarget}
          onConfirm={async (payload) => {
            await rescheduleTrialApi(rescheduleTrialSeed.id, payload);
            toast.success('Probestunde erfolgreich verschoben');
            setRescheduleTrialOpen(false);
            setRescheduleTrialSeed(null);
            setRescheduleTrialTarget(null);
            await refetchSchedule();
            await timetableHookData.refetch();
            await refetchCalendarTrials();
          }}
        />
      )}
      {renderCreateDialog()}
      {renderTerminateDialog()}
      {kuendigungTeilnahmeId > 0 && (
        <KuendigungWizardModal
          open={kuendigungWizardOpen}
          onOpenChange={setKuendigungWizardOpen}
          teilnahmeId={kuendigungTeilnahmeId}
          standortId={selectedStandortId || 0}
          onCreated={() => handleWizardDone()}
        />
      )}
      {renderChangeCourseDialog()}
      <TeacherChangeWizardModal open={teacherChangeOpen} onOpenChange={setTeacherChangeOpen} seed={teacherChangeSeed} />
    </div>;
};
export default ScheduleManager;
